import type { QueuedTransaction } from './types'
import { compareTransactions, createMemoryStorage, type QueueStorage } from './storage'

const DB_NAME = 'dock-automation-barcoding'
const DB_VERSION = 1
const STORE = 'sync-queue'

/**
 * IndexedDB adapter — the real store.
 *
 * IndexedDB rather than localStorage because these tablets get power-cycled and
 * jostled: we need something that survives a reload, holds structured records,
 * has room for a long outage, and — critically on a bouncing forklift — does
 * not block the UI thread the way synchronous localStorage does.
 */
export function createIndexedDbStorage(): QueueStorage {
  let dbPromise: Promise<IDBDatabase> | null = null

  function open(): Promise<IDBDatabase> {
    if (!dbPromise) {
      dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = () => {
          const db = request.result
          if (!db.objectStoreNames.contains(STORE)) {
            db.createObjectStore(STORE, { keyPath: 'id' })
          }
        }
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
    }
    return dbPromise
  }

  async function withStore<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await open()
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      const request = run(tx.objectStore(STORE))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async function readAll(): Promise<QueuedTransaction[]> {
    const rows = await withStore<QueuedTransaction[]>('readonly', store => store.getAll())
    return rows.sort(compareTransactions)
  }

  return {
    async put(transaction) {
      await withStore('readwrite', store => store.put(transaction))
    },
    all: readAll,
    async due(now) {
      const rows = await readAll()
      return rows.filter(op => op.state === 'pending' && op.nextAttemptAt <= now)
    },
    async remove(id) {
      await withStore('readwrite', store => store.delete(id))
    },
    async clear() {
      await withStore('readwrite', store => store.clear())
    },
  }
}

/**
 * The store the app should use: IndexedDB when the browser allows it, memory
 * otherwise. Callers get a working queue either way; `durable` tells the UI
 * whether pending work would survive a reload, which an operator deserves to
 * know before they keep scanning.
 */
export function createQueueStorage(): { storage: QueueStorage, durable: boolean } {
  const available = typeof indexedDB !== 'undefined'
  if (!available) {
    return { storage: createMemoryStorage(), durable: false }
  }
  return { storage: createIndexedDbStorage(), durable: true }
}
